/**
 * <region-dropdown> — cl-sort-style custom dropdown over a native <select>.
 * See snippets/region-dropdown.liquid. Shared by the footer selector and the region banner.
 *
 * The native <select> is the no-JS base AND the single source of truth for `country_code` — it
 * stays enabled and submittable even while hidden. This element hides it, reveals the custom
 * dropdown, and writes chosen values back into it.
 *
 * data-submit-on-change  -> commit immediately on choose (footer selector).
 * (absent)               -> just set the value; a separate submit button commits it (banner ACCEPT).
 *
 * On connect it MIRRORS the select's current value into the trigger label. That's how the banner's
 * pre-selection works: region-banner.js sets select.value on the cloned fragment BEFORE appending,
 * then this element upgrades and picks it up.
 *
 * Interaction mirrors the sort dropdown (assets/cl-collection.js): `.is-open` on the root,
 * aria-expanded on the trigger, click-outside + Escape to close, roving arrow-key focus.
 */
(function () {
  if (customElements.get('region-dropdown')) return;

  // The strong "I've decided" memory, shared with assets/region-banner.js. Written whenever the
  // shopper COMMITS a region — from the footer selector or the banner's ACCEPT. Once it exists,
  // the banner never nudges them again. (This is the Mango model; see the reference-sites doc.)
  var CHOICE_KEY = 'ss-region-choice';

  class RegionDropdown extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.select = this.querySelector('[data-region-select]');
      this.nativeBlock = this.querySelector('[data-region-native]');
      this.custom = this.querySelector('[data-region-custom]');
      this.trigger = this.querySelector('[data-region-trigger]');
      this.panel = this.querySelector('[data-region-panel]');
      this.triggerCur = this.querySelector('[data-region-trigger-cur]');
      this.triggerName = this.querySelector('[data-region-trigger-name]');
      if (!this.select || !this.custom || !this.trigger || !this.panel) return;

      this.options = Array.prototype.slice.call(this.querySelectorAll('[data-region-option]'));
      this.submitOnChange = this.hasAttribute('data-submit-on-change');

      // Swap the native control for the enhanced dropdown.
      this.custom.hidden = false;
      if (this.nativeBlock) this.nativeBlock.hidden = true;
      this.classList.add('is-enhanced');

      this.syncFromSelect();

      this._onTriggerClick = this.onTriggerClick.bind(this);
      this._onOptionClick = this.onOptionClick.bind(this);
      this._onKeydown = this.onKeydown.bind(this);
      this._onDocClick = this.onDocClick.bind(this);
      this._onSubmit = this.recordChoice.bind(this);

      this.trigger.addEventListener('click', this._onTriggerClick);
      this.panel.addEventListener('click', this._onOptionClick);
      this.addEventListener('keydown', this._onKeydown);
      document.addEventListener('click', this._onDocClick);

      // Catches BOTH commits: the banner's native ACCEPT button and the footer's requestSubmit().
      if (this.form) this.form.addEventListener('submit', this._onSubmit);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      if (this.form) this.form.removeEventListener('submit', this._onSubmit);
    }

    // Record the explicit region choice. Survives the navigation that follows.
    recordChoice() {
      try {
        window.localStorage.setItem(
          CHOICE_KEY,
          JSON.stringify({ iso: (this.select.value || '').toUpperCase(), ts: Date.now() })
        );
      } catch (e) {
        /* ignore storage failures */
      }
    }

    // Mirror the native select's value into the trigger + option states.
    // The trigger is TWO spans (currency / country), so copy from the matching custom option — the
    // native <option> is plain text and would flatten the two-column layout back into one line.
    syncFromSelect() {
      var value = this.select.value;
      var match = null;

      for (var i = 0; i < this.options.length; i++) {
        var isSelected = this.options[i].getAttribute('data-value') === value;
        this.options[i].classList.toggle('is-selected', isSelected);
        if (isSelected) match = this.options[i];
      }
      if (!match) return;

      var cur = match.querySelector('[data-region-cur]');
      var name = match.querySelector('[data-region-name]');
      if (this.triggerCur && cur) this.triggerCur.textContent = cur.textContent;
      if (this.triggerName && name) this.triggerName.textContent = name.textContent;
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    // The selected country is hidden from the panel (`.is-selected { display: none }`) — the panel
    // only offers what you can switch TO. So focus + arrow-keys must skip it.
    get visibleOptions() {
      return this.options.filter(function (o) {
        return !o.classList.contains('is-selected');
      });
    }

    toggle(force) {
      var open = force != null ? force : !this.isOpen;
      var opts = this.visibleOptions;
      if (open && !opts.length) return; // nothing to switch to — don't open an empty panel

      this.classList.toggle('is-open', open);
      this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) opts[0].focus();
    }

    onTriggerClick(e) {
      e.preventDefault();
      this.toggle();
    }

    onDocClick(e) {
      if (this.isOpen && !this.contains(e.target)) this.toggle(false);
    }

    onOptionClick(e) {
      var option = e.target.closest('[data-region-option]');
      if (option && this.panel.contains(option)) this.choose(option);
    }

    choose(option) {
      var value = option.getAttribute('data-value');
      if (!value) return;

      var changed = value !== this.select.value;
      this.select.value = value;
      this.syncFromSelect();
      this.toggle(false);
      this.trigger.focus();

      if (!changed || !this.submitOnChange || !this.form) return;

      // Footer: commit straight away. Native submit -> POST /localization -> Shopify reloads.
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(); // fires 'submit' -> recordChoice() runs
      } else {
        this.recordChoice(); // form.submit() does NOT fire 'submit', so record it ourselves
        this.form.submit();
      }
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.isOpen) {
          this.toggle(false);
          this.trigger.focus();
        }
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!this.isOpen) {
          if (e.target === this.trigger) {
            e.preventDefault();
            this.toggle(true);
          }
          return;
        }
        e.preventDefault();
        this.moveFocus(e.key === 'ArrowDown' ? 1 : -1);
      }
    }

    moveFocus(dir) {
      var opts = this.visibleOptions;
      var count = opts.length;
      if (!count) return;
      var idx = opts.indexOf(document.activeElement);
      opts[(idx + dir + count) % count].focus();
    }
  }

  customElements.define('region-dropdown', RegionDropdown);
})();
